import { FormattedMessage } from 'react-intl'
import { isSameDay } from 'date-fns'

import { TrashIcon } from '@gto/icons-react/outline/trash'
import { XMarkIcon } from '@gto/icons-react/solid/x-mark'
import { Button } from '@gto/ui/button'
import { Chip } from '@gto/ui/chip'
import { Divider } from '@gto/ui/divider'
import { typography } from '@gto/ui/typography'

export type DateRangeValue = [Date | null, Date | null]

export type DateRangePreset = {
  label: string
  getValue: () => DateRangeValue
}

export type DateRangePresetsProps = {
  items: DateRangePreset[]
  value?: DateRangeValue
  onSelect: (value: DateRangeValue) => void
  /** Wraps the rail horizontally and drops the divider, for the mobile sheet. */
  horizontal?: boolean
}

const EMPTY_RANGE: DateRangeValue = [null, null]

/**
 * Which preset the current value corresponds to, or `-1`.
 *
 * Compared by day rather than by identity because the value round-trips through
 * URL search params on most consumers, so the `Date` objects are never the ones
 * the preset produced.
 */
function getActiveIndex(items: DateRangePreset[], value?: DateRangeValue): number {
  const [start, end] = value ?? EMPTY_RANGE

  if (!start) {
    return -1
  }

  return items.findIndex((item) => {
    const [itemStart, itemEnd] = item.getValue()

    if (!itemStart || !isSameDay(itemStart, start)) {
      return false
    }

    return itemEnd == null ? end == null : end != null && isSameDay(itemEnd, end)
  })
}

/** Preset rail beside the calendar — "Last 7 days" and friends, plus Clear all. */
export function DateRangePresets({
  items,
  value,
  onSelect,
  horizontal = false,
}: DateRangePresetsProps) {
  if (items.length === 0) {
    return null
  }

  const activeIndex = getActiveIndex(items, value)

  return (
    <div className="flex">
      <div
        className={
          horizontal
            ? 'flex w-full flex-row flex-wrap gap-2'
            : 'flex w-full flex-1 flex-col justify-between gap-4 p-4'
        }
      >
        <div className={horizontal ? 'contents' : 'flex flex-col items-start gap-2'}>
          {items.map((item, index) => (
            <Chip
              key={item.label}
              color={index === activeIndex ? 'primary' : 'default'}
              selected={index === activeIndex}
              variant={horizontal || index === activeIndex ? 'outlined' : 'filled'}
              label={
                // The mobile sheet's presets are 16px (Figma 694:355345); medium chips stay 12px.
                horizontal ? (
                  <span className={typography['chip/label-large']}>{item.label}</span>
                ) : (
                  item.label
                )
              }
              onClick={() => {
                onSelect(item.getValue())
              }}
            />
          ))}
        </div>

        {horizontal ? (
          <Button
            color="secondary"
            startIcon={<TrashIcon className="size-5" />}
            variant="contained"
            onClick={() => {
              onSelect(EMPTY_RANGE)
            }}
          >
            <FormattedMessage id="common.clearAll" />
          </Button>
        ) : (
          <Button
            className="w-fit"
            color="secondary"
            size="small"
            startIcon={<XMarkIcon className="size-4" />}
            variant="text"
            onClick={() => {
              onSelect(EMPTY_RANGE)
            }}
          >
            <FormattedMessage id="common.clearAll" />
          </Button>
        )}
      </div>

      {!horizontal && <Divider orientation="vertical" />}
    </div>
  )
}
